import {reactive} from 'vue'
import {Location, AreaContent, AreaOptions} from './MapDrawLayerContents.ts'


/**
 * Google Maps描画レイヤー管理クラス
 * Googleマップの単一データレイヤー（google.maps.Data）の初期化と管理（複数のレイヤー管理はTodo）
 * 複数種類の描画コンテンツ（エリア、マーカーなど）の統合的な管理と連携
 * アプリケーション全体からのアクセスポイントの提供（シングルトン）
 * 描画モードなど全体的な状態管理
 * 各コンテンツに必要な共有コンテキスト（map, layer）の提供
 * 地図要素表示など高レベル操作の統合インターフェース提供
 */
export class MapDrawLayerManager {
  private map: google.maps.Map | null = null
  private layer: google.maps.Data | null = null
  private isInitialized = false
  private static _instance: MapDrawLayerManager | null = null

  // コンテンツをパブリックにして直接アクセス可能に
  public readonly area: AreaContent
  public state = reactive({
    enabled: false
  })

  /**
   * MapDrawLayerManagerのコンストラクタ
   *
   * @param {google.maps.Map | null} map - 初期化に使用するGoogleマップインスタンス（オプション）
   */
  constructor(map: google.maps.Map | null = null) {
    if (MapDrawLayerManager._instance) {
      return MapDrawLayerManager._instance
    }

    MapDrawLayerManager._instance = this

    // コンテンツの初期化
    this.area = new AreaContent()

    if (map) {
      this.initialize(map)
    }
  }

  /**
   * 初期化
   * データレイヤーを作成し、各コンテンツにコンテキストを提供します
   *
   * @param {google.maps.Map} map - 初期化に使用するGoogleマップインスタンス
   */
  initialize(map: google.maps.Map): void {
    if (this.isInitialized) {
      return
    }

    try {
      this.map = map
      this.layer = new google.maps.Data()
      this.layer.setMap(this.map)
      this.area.configureContext(this.map, this.layer, this)
      this.isInitialized = true
      console.info('MapDrawLayerManager initialized successfully')
    } catch (error) {
      console.error('MapDrawLayerManager initialization failed:', error)
      this.isInitialized = false
    }
  }

  /**
   * 管理しているデータレイヤーを取得する
   *
   * @returns {google.maps.Data | null} - 初期化済みのデータレイヤー、または未初期化の場合はnull
   */
  getDataLayer(): google.maps.Data | null {
    if (!this.isInitialized && this.layer === null) {
      console.warn('MapDrawLayerManager: Layer not initialized')
      return null
    }

    return this.layer
  }

  /**
   * 指定された位置に地図要素（ポリゴンエリアと中心マーカー）を表示する
   * 表示可能状態のチェックなどはAreaContent側で行う
   *
   * @param {Object} params - パラメータオブジェクト
   * @param {Location} params.location - 表示する位置情報（緯度経度座標）
   * @param {Partial<AreaOptions>} [params.options] - 表示オプション
   * @public
   */
  public displayMapElements({location, options = {}}: {
    location: Location;
    options?: Partial<AreaOptions>
  }): void {
    try {
      this.area.draw(location, options)
    } catch (error) {
      console.error('指定地点表示エラー:', error)
    }
  }
}

// シングルトンインスタンス
export const mapDrawLayerManager = new MapDrawLayerManager()